import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import api from "../lib/api";
import { ArrowLeft } from "lucide-react";
import RestorationTimeline from "../components/RestorationTimeline";
import Lightbox from "../components/Lightbox";

export default function ComputerDetail() {
    const { id } = useParams();
    const [c, setC] = useState(null);
    const [notFound, setNotFound] = useState(false);
    const [lbIndex, setLbIndex] = useState(null);

    useEffect(() => {
        (async () => {
            try {
                const { data } = await api.get(`/computers/${id}`);
                setC(data);
            } catch {
                setNotFound(true);
            }
        })();
    }, [id]);

    if (notFound) {
        return (
            <div className="mx-auto max-w-4xl px-6 py-32 text-center">
                <div className="overline">// 404</div>
                <h1 className="mt-4 font-serif text-5xl">Record not found</h1>
                <Link to="/" className="mt-8 inline-block font-mono text-xs text-phosphor-amber uppercase tracking-[0.25em]">
                    ‹ Return to archive
                </Link>
            </div>
        );
    }

    if (!c) {
        return (
            <div className="py-32 text-center font-mono text-xs uppercase tracking-[0.3em] text-muted-foreground">
                Fetching record<span className="terminal-caret" />
            </div>
        );
    }

    const specs = [
        ["Manufacturer", c.manufacturer],
        ["Year", c.year],
        ["Country", c.country],
        ["CPU", c.cpu],
        ["RAM", c.ram],
        ["Storage", c.storage],
        ["Condition", c.condition],
        ["Original Price", c.original_price ? `$${c.original_price.toLocaleString()}` : null],
        ["Current Value", c.current_value ? `$${c.current_value.toLocaleString()}` : null],
    ].filter(([, v]) => v);

    return (
        <div data-testid="computer-detail-page" className="mx-auto max-w-[1400px] px-6 lg:px-10 pt-8">
            <Link
                to="/"
                data-testid="back-to-archive"
                className="inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.3em] text-muted-foreground hover:text-phosphor-amber transition-colors"
            >
                <ArrowLeft className="h-3 w-3" /> Return to archive
            </Link>

            <div className="mt-10 grid grid-cols-1 lg:grid-cols-12 gap-12">
                {/* Left: image gallery (sticky) */}
                <div className="lg:col-span-6">
                    <div className="lg:sticky lg:top-24">
                        {(() => {
                            const photos = (c.images && c.images.length > 0) ? c.images : (c.image_url ? [c.image_url] : []);
                            if (photos.length === 0) return null;
                            return (
                                <>
                                    <button
                                        type="button"
                                        data-testid="detail-cover-btn"
                                        onClick={() => setLbIndex(0)}
                                        className="block w-full scanlines border border-border hover:border-phosphor-amber transition-colors"
                                    >
                                        <img
                                            src={photos[0]}
                                            alt={c.name}
                                            className="w-full h-auto object-cover"
                                        />
                                    </button>
                                    {photos.length > 1 && (
                                        <div data-testid="detail-thumbs" className="mt-3 grid grid-cols-5 gap-2">
                                            {photos.map((src, idx) => (
                                                <button
                                                    key={`${src}-${idx}`}
                                                    type="button"
                                                    data-testid={`detail-thumb-${idx}`}
                                                    onClick={() => setLbIndex(idx)}
                                                    className={`scanlines border transition-colors ${idx === 0 ? "border-phosphor-amber" : "border-border hover:border-phosphor-amber"}`}
                                                >
                                                    <img src={src} alt="" className="w-full h-16 object-cover" />
                                                </button>
                                            ))}
                                        </div>
                                    )}
                                </>
                            );
                        })()}
                        <div className="mt-3 font-mono text-[10px] uppercase tracking-[0.25em] text-muted-foreground">
                            Ref. #{c.id.slice(0, 8).toUpperCase()} · {c.manufacturer} · {c.year}
                        </div>
                    </div>
                </div>

                {/* Right: editorial + specs */}
                <div className="lg:col-span-6">
                    <div className="overline">// Exhibit</div>
                    <h1
                        data-testid="detail-title"
                        className="mt-3 font-serif text-6xl md:text-7xl leading-[0.95] tracking-tight text-foreground"
                    >
                        {c.name}
                    </h1>
                    <div className="mt-4 flex flex-wrap items-center gap-3 font-mono text-[10px] uppercase tracking-[0.25em] text-muted-foreground">
                        <span className="border border-phosphor-amber text-phosphor-amber px-2 py-1">
                            {c.year}
                        </span>
                        <span>·</span>
                        <span>{c.manufacturer}</span>
                        {c.condition && (
                            <>
                                <span>·</span>
                                <span className="text-phosphor-green">◉ {c.condition}</span>
                            </>
                        )}
                    </div>

                    {c.story && (
                        <p className="mt-10 font-serif text-2xl leading-snug text-foreground/90 first-letter:font-serif first-letter:text-6xl first-letter:float-left first-letter:mr-3 first-letter:leading-none first-letter:text-phosphor-amber">
                            {c.story}
                        </p>
                    )}

                    <div className="mt-12 border border-border">
                        <div className="border-b border-border bg-muted/40 px-6 py-3 flex items-center justify-between">
                            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-phosphor-amber">
                                &gt; SPECIFICATIONS.DAT
                            </div>
                            <div className="font-mono text-[10px] text-muted-foreground">READ-ONLY</div>
                        </div>
                        <dl className="divide-y divide-border">
                            {specs.map(([k, v]) => (
                                <div key={k} className="grid grid-cols-12 px-6 py-4">
                                    <dt className="col-span-4 font-mono text-[10px] uppercase tracking-[0.25em] text-muted-foreground">
                                        {k}
                                    </dt>
                                    <dd className="col-span-8 font-mono text-sm text-foreground">
                                        {v}
                                    </dd>
                                </div>
                            ))}
                        </dl>
                    </div>

                    {c.tags && c.tags.length > 0 && (
                        <div className="mt-10 flex flex-wrap gap-2">
                            {c.tags.map((t) => (
                                <span
                                    key={t}
                                    className="border border-border px-3 py-1 font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground"
                                >
                                    #{t}
                                </span>
                            ))}
                        </div>
                    )}

                    <RestorationTimeline computerId={c.id} />

                    {(c.acquisition_source || c.acquisition_date || c.price_paid || c.insurance_value || c.provenance_notes) && (
                        <div data-testid="provenance-panel" className="mt-12 border border-border">
                            <div className="border-b border-border bg-muted/40 px-6 py-3 flex items-center justify-between">
                                <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-phosphor-amber">
                                    &gt; PROVENANCE.CHAIN
                                </div>
                                <div className="font-mono text-[10px] text-muted-foreground">CUSTODY</div>
                            </div>
                            <dl className="divide-y divide-border">
                                {c.acquisition_source && (
                                    <ProvRow label="Acquired from" value={c.acquisition_source} />
                                )}
                                {c.acquisition_date && (
                                    <ProvRow label="Acquisition date" value={c.acquisition_date} />
                                )}
                                {typeof c.price_paid === "number" && (
                                    <ProvRow label="Price paid" value={`$${c.price_paid.toLocaleString()}`} accent="green" />
                                )}
                                {typeof c.insurance_value === "number" && (
                                    <ProvRow label="Insured for" value={`$${c.insurance_value.toLocaleString()}`} accent="amber" />
                                )}
                            </dl>
                            {c.provenance_notes && (
                                <div className="border-t border-border px-6 py-5">
                                    <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-muted-foreground mb-2">Notes</div>
                                    <p className="font-sans text-sm text-foreground/85 leading-relaxed whitespace-pre-wrap">
                                        {c.provenance_notes}
                                    </p>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>

            {lbIndex !== null && (
                <Lightbox
                    images={(c.images && c.images.length > 0) ? c.images : (c.image_url ? [c.image_url] : [])}
                    index={lbIndex}
                    onClose={() => setLbIndex(null)}
                    onChange={setLbIndex}
                />
            )}
        </div>
    );
}

function ProvRow({ label, value, accent }) {
    const color = accent === "green" ? "text-phosphor-green" : accent === "amber" ? "text-phosphor-amber" : "text-foreground";
    return (
        <div className="grid grid-cols-12 px-6 py-4">
            <dt className="col-span-4 font-mono text-[10px] uppercase tracking-[0.25em] text-muted-foreground">{label}</dt>
            <dd className={`col-span-8 font-mono text-sm ${color}`}>{value}</dd>
        </div>
    );
}
